import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { WarningOutlined } from "@mui/icons-material";
import { FeatherIcon } from "component/shared/feather-icon";
import { Dialog } from "component/shared/dialog";
import { styles } from "./style";

/**
 * MAX-3860 activation confirmation, extracted from the Campaigns page so it can take a second
 * variant. MAX-4201 adds `blocked`: shown instead of the confirmation (never both) when a
 * Market-source campaign has no Market, sending the agent to Settings to choose one.
 */
export type ActivationDialogVariant = "confirm" | "blocked";

interface Props extends OwnProps, WithStyles<typeof styles> {}

interface OwnProps {
	open: boolean;
	variant: ActivationDialogVariant;
	onClose: () => void;
	onConfirm: () => void;
	onGoToSettings: () => void;
}

const Component: React.FC<Props> = ({ classes = {}, open, variant, onClose, onConfirm, onGoToSettings }) => {
	const blocked = variant === "blocked";
	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
		>
			<Mui.DialogTitle className={classes.warningDialogTitle}>
				<WarningOutlined style={{ color: '#f59e0b' }} />
				<Mui.Typography variant="h6" style={{ fontWeight: 700 }}>
					{blocked ? "Select a Market before activating" : "Before activating this campaign"}
				</Mui.Typography>
				<div className={classes.headerSpacer} />
				<Mui.IconButton
					size="small"
					aria-label="Close"
					onClick={onClose}
				>
					<FeatherIcon><Icons.X size={20} /></FeatherIcon>
				</Mui.IconButton>
			</Mui.DialogTitle>
			<Mui.DialogContent>
				{blocked ? (
					<>
						<Mui.Typography gutterBottom>
							This campaign uses Market-based smart content, and no Market is selected.
						</Mui.Typography>
						<Mui.Typography gutterBottom>
							Choose a Market in Settings, then activate the campaign.
						</Mui.Typography>
					</>
				) : (
					<>
						<Mui.Typography gutterBottom>
							Make sure you've reviewed your messages, settings, and subscribers before activating — once active, the campaign will begin sending.
						</Mui.Typography>
						<Mui.Typography gutterBottom>
							If resuming mid-sequence, any message that became due while this campaign was inactive will <strong>send immediately</strong>. All subsequent messages will then send on schedule.
						</Mui.Typography>
					</>
				)}
			</Mui.DialogContent>
			<Mui.DialogActions style={{ padding: '16px 24px' }}>
				<Mui.Button variant="outlined" color="primary" onClick={onClose}>
					Cancel
				</Mui.Button>
				{blocked ? (
					<Mui.Button variant="contained" color="primary" onClick={onGoToSettings}>
						Go to Settings
					</Mui.Button>
				) : (
					<Mui.Button variant="contained" color="primary" onClick={onConfirm}>
						Activate Campaign
					</Mui.Button>
				)}
			</Mui.DialogActions>
		</Dialog>
	);
};

export const ActivationDialog = withStyles(Component, styles);
